import React from "react";
import { Link } from "react-router-dom";
import { observer } from "mobx-react-lite";
import ApiData from "../../api/ApiData";
import ReportData from "../../api/ReportData";
import RegionBtn from "../company/RegionBtn";
import {
  buildLicensedServersForInn,
  formatDashboardSessionDateTime,
  formatDashboardSubscription,
  getDashboardCardStripeLevel,
  getDashboardEntityCountry,
  getSessionIndicatorLevel,
  getSessionStatusDotClass,
} from "../../utils/myCompaniesDashboardUtils";
import {
  EVENT_TOTALS_KEYS,
  EVENT_TOTALS_SHORT_LABELS,
  formatDefectWithShareDisplay,
  formatEventTotalDisplay,
} from "../../utils/eventTotalsSaUtils";

function EventTotalsDashboardSkeleton() {
  return (
    <div
      className="event-totals-dashboard-skeleton"
      aria-busy="true"
      aria-label="Загрузка статистики событий"
    >
      <div className="event-totals-dashboard-skeleton-row" />
      <div className="event-totals-dashboard-skeleton-row event-totals-dashboard-skeleton-row--short" />
    </div>
  );
}

function EventTotalsDashboardGrid({ totals }) {
  return (
    <div className="event-totals-dashboard-grid">
      {EVENT_TOTALS_KEYS.map((key) => (
        <div key={key} className="event-totals-dashboard-item">
          <span className="event-totals-dashboard-label">
            {EVENT_TOTALS_SHORT_LABELS[key]}
          </span>
          <span className="event-totals-dashboard-value">
            {key === "DEFECT"
              ? formatDefectWithShareDisplay(totals)
              : formatEventTotalDisplay(totals?.[key])}
          </span>
        </div>
      ))}
    </div>
  );
}

const STRIPE_CLASS = {
  neutral: "db-card--stripe-neutral",
  green: "db-card--stripe-green",
  yellow: "db-card--stripe-yellow",
  red: "db-card--stripe-red",
};

export const DashboardEntityCard = observer(function DashboardEntityCard({
  company,
  canShowLicensedServers,
  canViewEventTotals = false,
  eventTotalsMonth,
  eventTotalsAvg,
  eventTotalsLoading = false,
  eventTotalsError = false,
}) {
  const inn = company.inn;
  const linkTo = company.is_partner ? `/partners/${inn}` : `/company/${inn}`;
  const subscription = formatDashboardSubscription(company.subscription_rel);
  const badgeModifier = company.is_partner ? "partner" : "company";
  const badgeLabel = company.is_partner ? "Партнёр" : "Компания";
  const companyName = company.name_company ?? "";
  const country = getDashboardEntityCountry(company);

  let serversSectionBody = null;
  let serverLevels = [];
  const serversPending =
    canShowLicensedServers && !ReportData.versionReportSourcesReady;

  if (!canShowLicensedServers) {
    serversSectionBody = (
      <div className="servers-no-rights">Недостаточно прав</div>
    );
    serverLevels = [];
  } else if (serversPending) {
    serversSectionBody = (
      <div
        className="servers-skeleton"
        aria-busy="true"
        aria-label="Загрузка данных о серверах"
      >
        <div className="servers-skeleton-row" />
        <div className="servers-skeleton-row servers-skeleton-row--short" />
      </div>
    );
    serverLevels = null;
  } else {
    const servers = buildLicensedServersForInn(
      inn,
      ApiData.soft_all_data,
      ApiData.licensing_all,
    );
    serverLevels = servers.map((row) =>
      getSessionIndicatorLevel(row.last_session),
    );
    if (servers.length === 0) {
      serversSectionBody = (
        <div className="no-servers">Нет лицензированных серверов SRV</div>
      );
    } else {
      serversSectionBody = servers.map((row, index) => {
        const level = getSessionIndicatorLevel(row.last_session);
        const sessionText = formatDashboardSessionDateTime(row.last_session);
        return (
          <div
            key={`${row.displayName}-${row.software_version}-${index}`}
            className="server-item"
          >
            <div className="server-meta">
              <span
                className={`status-dot ${getSessionStatusDotClass(level)}`}
                aria-hidden="true"
              />
              <span className="server-name">{row.displayName}</span>
              <span className="server-name server-name--version">
                {row.software_version}
              </span>
            </div>
            <div
              className={
                sessionText == null
                  ? "server-version server-version--muted"
                  : "server-version"
              }
            >
              {sessionText ?? "Нет данных о сессии"}
            </div>
          </div>
        );
      });
    }
  }

  const stripeLevel = getDashboardCardStripeLevel({
    isSubscriptionActive: subscription.isActive,
    serverLevels,
    serversPending,
  });

  return (
    <Link
      to={linkTo}
      className="dashboard-entity-card-link"
      aria-label={`${badgeLabel}: ${companyName}`}
    >
      <div className={`db-card ${STRIPE_CLASS[stripeLevel]}`}>
        <div className="db-card-title-row">
          <span className={`type-badge ${badgeModifier}`}>{badgeLabel}</span>
          <RegionBtn region={country} badgeV4 />
        </div>
        <h3 className="org-name">{companyName}</h3>
        <div
          className={
            subscription.isActive
              ? "sub-status active"
              : "sub-status inactive"
          }
        >
          {subscription.text}
        </div>
        <div className="servers-section">
          <div className="servers-title">СЕРВЕРЫ SRV</div>
          {serversSectionBody}
        </div>
        {canViewEventTotals ? (
          <div className="event-totals-dashboard-wrap">
            <div className="event-totals-dashboard-section">
              <div className="servers-title">События (за месяц)</div>
              {eventTotalsLoading ? (
                <EventTotalsDashboardSkeleton />
              ) : eventTotalsError ? (
                <p className="event-totals-dashboard-muted">
                  Не удалось загрузить статистику
                </p>
              ) : (
                <EventTotalsDashboardGrid totals={eventTotalsMonth} />
              )}
            </div>
            <div
              className="event-totals-dashboard-section event-totals-dashboard-section--avg"
            >
              <div className="servers-title">События (ср./сутки, месяц)</div>
              {eventTotalsLoading ? (
                <EventTotalsDashboardSkeleton />
              ) : eventTotalsError ? null : (
                <EventTotalsDashboardGrid totals={eventTotalsAvg} />
              )}
            </div>
          </div>
        ) : null}
      </div>
    </Link>
  );
});
